// OnboardingGate — decides if the first-run wizard should mount this session.
//
// Triggers when ALL of the following are true:
//   - User is authenticated
//   - User has not completed onboarding (localStorage gate per-userId; backend
//     `me.onboardingCompleted` field is the next step per audit doc 19 §3.7)
//   - Current route is not the wizard or auth flow
//   - Viewport is mobile OR app is running standalone (it's an app-mode flow)
//
// This component lives next to InstallPrompt in App.tsx. It renders the
// orchestrator as a portal-equivalent overlay (z-index above everything).

import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useAppMode } from '@/hooks/useAppMode'
import OnboardingOrchestrator from './OnboardingOrchestrator'
import { isOnboardingCompleted } from './useOnboarding'

const SUPPRESS_PATHS = [
  /^\/autentificare/,
  /^\/inregistrare/,
  /^\/login/,
  /^\/register/,
  /^\/oauth/,
  /^\/reset/,
]

type Role = 'CLIENT' | 'WORKER' | 'COMPANY' | 'ADMIN' | 'GLOBAL_ADMIN'

export default function OnboardingGate() {
  const { user, isAuthenticated } = useAuth()
  const mode = useAppMode()
  const { pathname } = useLocation()
  const [dismissed, setDismissed] = useState(false)

  if (!isAuthenticated || !user) return null
  if (dismissed) return null
  if (SUPPRESS_PATHS.some((re) => re.test(pathname))) return null
  if (!mode.isMobileViewport && !mode.isStandalone) return null
  if (isOnboardingCompleted(user.id)) return null

  return (
    <OnboardingOrchestrator
      role={(user.role as Role | undefined) ?? null}
      userId={user.id}
      onCompleted={() => setDismissed(true)}
    />
  )
}
